import { createClient, Session } from "@supabase/supabase-js";
import { Database } from "../types/supabase";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
export const supabase = createClient<Database>(supabaseUrl, supabaseKey);

// Auth functions remain the same, using supabase client
export const updateEmail = async (
  userEmail: string
): Promise<{ success: boolean; error: string }> => {
  const { error } = await supabase.auth.updateUser({
    email: userEmail,
  });
  return error
    ? { success: false, error: error.message }
    : { success: true, error: "" };
};

export const updatePassword = async (
  userPassword: string,
  // Optional. Required by the Supabase project when
  // SECURITY_UPDATE_PASSWORD_REQUIRE_CURRENT_PASSWORD is enabled
  // (true on cloud staging/prod). The reset-password flow leaves
  // this undefined because the user is authenticated via the
  // recovery link — no "current password" exists for them.
  currentPassword?: string
): Promise<{ success: boolean; error: string }> => {
  const { error } = await supabase.auth.updateUser({
    password: userPassword,
    ...(currentPassword ? { current_password: currentPassword } : {}),
  });
  return error
    ? { success: false, error: error.message }
    : { success: true, error: "" };
};

export const updateName = async (
  userId: string,
  newName: string
): Promise<{ success: boolean; error: string }> => {
  const { error } = await supabase
    .from("users")
    .update({
      full_name: newName,
    })
    .eq("user_id", userId);

  return {
    success: !error,
    error: error ? error.message : "",
  };
};

export const updateUsername = async (
  userId: string,
  username: string
): Promise<{ success: boolean; error: string }> => {
  const { error } = await supabase
    .from("users")
    .update({
      username: username,
    })
    .eq("user_id", userId);

  return {
    success: !error,
    error: error ? error.message : "",
  };
};

export const updateUserProfile = async (
  userId: string,
  profile: {
    fullName?: string;
    username?: string;
    avatarUrl?: string;
  }
): Promise<{ success: boolean; error: string }> => {
  const { error } = await supabase
    .from("users")
    .update({
      full_name: profile.fullName,
      username: profile.username,
      avatar_url: profile.avatarUrl,
    })
    .eq("user_id", userId);

  return {
    success: !error,
    error: error ? error.message : "",
  };
};

export const signInUser = async (email: string, password: string) => {
  return supabase.auth.signInWithPassword({
    email,
    password,
  });
};

export const getSessionUserId = async (): Promise<{
  error: string;
  userId: string;
}> => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error) {
    console.error("Error fetching session:", error);
    return { error: error.message, userId: "" };
  }
  return { error: "", userId: session?.user.id || "" };
};

export interface SignUpData {
  email: string;
  password: string;
  // Optional since the signup form went email+password-only: when
  // absent, the server derives a username from the email local-part
  // (with collision suffixing) — the same path OAuth identities take —
  // and name is added later on the profile page.
  fullName?: string;
  username?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  data?: any;
}

export const signUp = async (signUpData: SignUpData): Promise<AuthResult> => {
  const { data, error } = await supabase.auth.signUp({
    email: signUpData.email,
    password: signUpData.password,
    options: {
      data: {
        ...(signUpData.fullName ? { full_name: signUpData.fullName } : {}),
        ...(signUpData.username ? { username: signUpData.username } : {}),
      },
    },
  });

  if (error) {
    return { success: false, error: error.message };
  } else if (data?.user?.identities?.length === 0) {
    return { success: false, error: "Someone with that email already exists" };
  }

  return { success: true, data };
};

export const signIn = async (loginData: LoginData): Promise<AuthResult> => {
  const { error } = await supabase.auth.signInWithPassword({
    email: loginData.email,
    password: loginData.password,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
};

export const signInWithGoogle = async (): Promise<AuthResult> => {
  // On success this navigates the tab to Google's consent screen and
  // the promise's caller never sees the app again until the OAuth
  // redirect lands back on the app origin — where detectSessionInUrl
  // picks the tokens out of the URL and fires SIGNED_IN, entering the
  // same AAL gate + bootstrap as a password sign-in. The return value
  // only matters for failures (provider disabled, network down).
  const appUrl =
    import.meta.env.VITE_APP_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${appUrl}/` },
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
};

export const resetPassword = async (email: string): Promise<AuthResult> => {
  // VITE_APP_URL is the canonical app origin for the environment
  // (dev / staging / prod). Falls back to window.location.origin so
  // a misconfigured deploy still produces a working same-origin link
  // rather than silently routing users to production.
  const appUrl =
    import.meta.env.VITE_APP_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${appUrl}/resetpassword`,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
};

export const signOut = async (): Promise<AuthResult> => {
  // scope: 'local' signs out only the current device. The supabase-js
  // default is 'global', which revokes the refresh token server-side
  // and signs the user out across every device they're logged in on
  // — surprising semantics for what's labelled "Log out" in this app
  // (single-device intent). If we ever add a "sign out everywhere"
  // affordance, that's where scope: 'global' belongs.
  const { error } = await supabase.auth.signOut({ scope: "local" });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
};

export const onAuthStateChange = (
  callback: (event: string, session: Session | null) => void
) => {
  return supabase.auth.onAuthStateChange(callback).data.subscription;
};

// ---- MFA --------------------------------------------------------------

export interface MfaEnrollResult {
  factorId: string;
  qrCode: string; // SVG data: URL
  secret: string; // base32 secret for manual entry
  uri: string; // otpauth:// URI
}

/**
 * Begin enrollment: create an unverified TOTP factor. Returns the
 * material the UI needs to render the QR code and let the user enter
 * a verification code. The factor is not active until verifyMfa
 * succeeds. If the user abandons enrollment, the unverified factor
 * remains and can be cleaned up by calling unenrollMfa or via a
 * later attempt — Supabase tolerates multiple unverified factors per
 * user but listFactors filters them out by status.
 */
export const enrollMfa = async (
  friendlyName?: string
): Promise<{ success: true; data: MfaEnrollResult } | AuthResult> => {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    ...(friendlyName ? { friendlyName } : {}),
  });
  if (error || !data) {
    return { success: false, error: error?.message ?? "enroll failed" };
  }
  return {
    success: true,
    data: {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    },
  };
};

/**
 * Challenge + verify in one shot. On success the session is elevated
 * to AAL2 client-side; the access token in subsequent requests will
 * carry `aal: "aal2"`, satisfying RequireAAL2 on the API.
 */
export const verifyMfa = async (
  factorId: string,
  code: string
): Promise<AuthResult> => {
  const { data: challenge, error: challengeErr } =
    await supabase.auth.mfa.challenge({ factorId });
  if (challengeErr || !challenge) {
    return { success: false, error: challengeErr?.message ?? "challenge failed" };
  }
  const { error: verifyErr } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  });
  if (verifyErr) {
    return { success: false, error: verifyErr.message };
  }
  return { success: true };
};

/**
 * Remove a factor. Used both for "Unenroll" and for cleaning up an
 * abandoned-mid-enrollment unverified factor. After unenroll, the
 * caller is responsible for invoking deleteRecoveryCodes() on the
 * API server to wipe the orphaned recovery-code rows.
 */
export const unenrollMfa = async (factorId: string): Promise<AuthResult> => {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
};

export interface MfaFactor {
  id: string;
  friendlyName: string;
  createdAt: string;
}

/**
 * List the user's verified TOTP factors. Drives the "Enrolled / Not
 * enrolled" status in the profile MFA panel. Returns an empty array
 * when there are no verified factors (including the case where the
 * user has an abandoned unverified factor — those are excluded).
 */
export const listMfaFactors = async (): Promise<MfaFactor[]> => {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return [];
  return data.totp.map((f) => ({
    id: f.id,
    friendlyName: f.friendly_name ?? "",
    createdAt: f.created_at,
  }));
};

/**
 * Pull the current AAL from Supabase. Used by the AuthContext sign-in
 * pre-flight (Phase 2) and anywhere we need to know whether to gate
 * an action client-side. The server is the real source of truth via
 * the JWT `aal` claim; this is the corresponding client-side read.
 */
export const getCurrentAal = async (): Promise<{
  current: "aal1" | "aal2" | null;
  next: "aal1" | "aal2" | null;
}> => {
  const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  return {
    current: (data?.currentLevel ?? null) as "aal1" | "aal2" | null,
    next: (data?.nextLevel ?? null) as "aal1" | "aal2" | null,
  };
};
